import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Store } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/lib/cn';
import { enviarShowroom, type InventarioRow } from '../api';

/**
 * Move units from Disponible to Prest/Show. Mirrors PowerApps's
 * PopUp_EnviarShowRoomArticulo (Screen_Inventario.pa.yaml:1737+).
 * Creates a `23.Showrooms` row marking the placement event so the
 * Showroom activos tab surfaces it.
 */
export function EnviarShowroomDialog({
  row,
  onClose,
}: {
  row: InventarioRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [cantidadRaw, setCantidadRaw] = useState('');

  const cantidad = parseCantidad(cantidadRaw);
  const disponible = row.buckets.disponible;
  const exceeds = cantidad !== null && cantidad > disponible + 0.0001;
  const canSubmit =
    cantidad !== null && cantidad > 0 && !exceeds && disponible > 0;

  const mutation = useMutation({
    mutationFn: () =>
      enviarShowroom(row.id, {
        cantidad: cantidad ?? 0,
      }),
    onSuccess: (r) => {
      if (r.outcome === 'insufficient_disponible') {
        toast.error(r.message);
        return;
      }
      toast.success(`${r.cantidad} un. enviadas al showroom`);
      qc.invalidateQueries({ queryKey: ['inventario'] });
      onClose();
    },
    onError: () => toast.error('No se pudo enviar al showroom'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && !mutation.isPending && onClose()}>
      <DialogContent
        className="max-w-md"
        processing={mutation.isPending && 'Enviando a Showroom…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Store className="h-4 w-4" aria-hidden />
            Enviar a Showroom
          </DialogTitle>
          <DialogDescription>
            {row.fields.Articulo_I || 'artículo'} · Disponible:{' '}
            <strong>{disponible}</strong> un.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2">
            <Bucket label="Disponible" value={disponible} tone="green" />
            <ArrowRight className="h-5 w-5 text-muted-foreground" aria-hidden />
            <Bucket label="Prest/Show" value={row.buckets.prestShow} tone="rose" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="enviar-cantidad" className="text-sm">
              Cantidad a enviar <span className="text-destructive">*</span>
            </Label>
            <Input
              id="enviar-cantidad"
              type="number"
              inputMode="decimal"
              min={0}
              max={disponible}
              step="0.01"
              placeholder={`máx ${disponible}`}
              value={cantidadRaw}
              onChange={(e) => setCantidadRaw(e.target.value)}
              className={cn('tabular-nums', exceeds && 'border-destructive')}
              autoFocus
            />
            {exceeds && (
              <p className="text-xs text-destructive">
                Excede el stock Disponible ({disponible}).
              </p>
            )}
          </div>

          {cantidad !== null && cantidad > 0 && !exceeds && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              Después: Disponible {disponible - cantidad} · Prest/Show{' '}
              {row.buckets.prestShow + cantidad}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            className="gap-2 bg-rose-600 text-white hover:bg-rose-700"
          >
            <Store className="h-4 w-4" aria-hidden />
            Enviar a Showroom
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'rose';
}) {
  const cls =
    tone === 'green'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-rose-100 text-rose-700';
  return (
    <div className="flex flex-1 flex-col items-center">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'mt-1 inline-flex min-w-12 justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums',
          cls,
        )}
      >
        {value}
      </span>
    </div>
  );
}

function parseCantidad(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}
